import { createApiClient } from '../../../api';
import type { ApiClient, ApiClientOptions } from '../../../api';
import { backendApi } from '../../../constants/axiosInstance';

import { ctlErrorFrom } from './errors';

const CROSS_ORIGIN_ALLOWED = 'true';

export interface CtlEndpointSettings {
  readonly ctl: string | undefined;
  readonly core: string | undefined;
  readonly allowCrossOrigin: boolean;
}

const originOf = (url: string): string | null => {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
};

let warnedCrossOrigin = false;

const warnCrossOriginOnce = (ctl: string): void => {
  if (warnedCrossOrigin || !import.meta.env.DEV) return;
  warnedCrossOrigin = true;
  console.warn(
    `VITE_CTL_ENDPOINT (${ctl}) is not on the app API's origin, so the session cookie would not reach it. Using the app API instead; set VITE_CTL_CROSS_ORIGIN=true if that origin shares the session.`,
  );
};

export const ctlBaseUrlOf = ({ ctl, core, allowCrossOrigin }: CtlEndpointSettings): string | undefined => {
  const base = ctl?.trim();
  if (!base) return undefined;
  if (allowCrossOrigin) return base;
  const origin = originOf(base);
  if (origin !== null && origin === originOf(core?.trim() || '/')) return base;
  warnCrossOriginOnce(base);
  return undefined;
};

const configuredCtlBase = (): string | undefined =>
  ctlBaseUrlOf({
    ctl: import.meta.env.VITE_CTL_ENDPOINT,
    core: import.meta.env.VITE_APP_ENDPOINT,
    allowCrossOrigin: import.meta.env.VITE_CTL_CROSS_ORIGIN === CROSS_ORIGIN_ALLOWED,
  });

export const createCtlClient = (opts: Pick<ApiClientOptions, 'onSessionLost' | 'toError'> = {}): ApiClient =>
  createApiClient({ toError: ctlErrorFrom, ...opts, baseURL: configuredCtlBase() });

const joinedUrl = (base: string | undefined, path: string): string =>
  base ? `${base.replace(/\/?\/$/, '')}/${path.replace(/^\/+/, '')}` : path;

export const ctlUrlOf = (path: string): string => joinedUrl(configuredCtlBase() ?? backendApi, path);

export const { request } = createCtlClient();
